import ArrowUpward from '@material-ui/icons/ArrowUpward';
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import { withNamespaces } from 'react-i18next';
import { constants } from 'app-webtech-core';
import fscreen from 'fscreen';
import debounce from 'lodash/debounce';
import EventListener from 'react-event-listener';
import { ErrorBoundary } from 'app-modules/ui/components/ErrorBoundary';
import { HEADER_HEIGHT } from 'app-modules/base/constants';
import { MAX_BUFFERING_TIME_IN_MS, RETRIES_ON_ERROR, RETRY_RATE_IN_MS } from '../constants/const';
import { UnknownError } from './errors/UnknownError';
import { PlayerContext } from './PlayerContext';
import { PlayerCTA } from './PlayerCTA';
import { PlayerOverlay } from './PlayerOverlay';
import { VideoPlayer } from '../containers/VideoPlayer';
import { LoadingIndicator } from './LoadingIndicator';
import { ViralEndVideoNavigatorWrapper } from './ViralEndVideoNavigator';
import { AssetsFileResolver } from '../../application/containers/AssetsFileResolver';
import { checkMobileOperatingSystem } from 'app-modules/application/utils/checkMobileOperatingSystem';
import { VIRAL_CLIP_RESOLVE_TIME } from '../../viral/constants';

const ratio = (9 * 100) / 16;

const Wrapper = styled.div`
  height: ${({ internalFullscreen, isFullscreenSplashPage, isEmbed, isHeadless }) =>
    (internalFullscreen || isFullscreenSplashPage || isEmbed) && !isHeadless
      ? 'calc(100vh - 64px)'
      : isHeadless
      ? '100vh'
      : '65vh'};

  max-height: 0;
  transition: max-height 0.3s linear;
  width: 100%;

  .wrapper-bg,
  .control-btn,
  .channel-info {
    transition: opacity 0.2s linear;
    opacity: 0;
    &.volume-off {
      opacity: 1;
    }
  }

  ${({ canTouch }) =>
    !canTouch &&
    css`
      &:hover {
        .wrapper-bg,
        .control-btn {
          opacity: 1;
        }
      }
    `}

  ${({ playerIsFullscreen, isChatBarShown }) =>
    playerIsFullscreen &&
    isChatBarShown &&
    css`
      @media (min-width: 960px) {
        margin-top: calc((35vh - ${HEADER_HEIGHT}px) / 2);
      }
    `}

  ${({ playerIsFullscreen, isFullscreenSplashPage }) =>
    (playerIsFullscreen || isFullscreenSplashPage) &&
    css`
      margin-top: 0;
      max-height: ${ratio}vw;
      box-shadow: none;
      position: relative;

      &:hover {
        .channel-info {
          opacity: 1;
        }
      }

      ${({ canTouch, uiControls }) =>
        uiControls &&
        (canTouch
          ? css`
              .control-btn {
                opacity: 1;
              }
            `
          : css`
              .control-btn {
                opacity: 0;
              }
            `)}
    `}

  ${({ hidePlayer }) =>
    hidePlayer &&
    css`
      display: none;
    `}

    // on mobile landscape, expand player to full screen
    @media (max-width: 767px) and (orientation: landscape) {
    ${({ isMobile }) =>
      isMobile &&
      css`
        margin-top: 0;
        height: 100%;
        max-height: 100vh;
        box-shadow: none;
        position: relative;
      `}

    &:hover {
      .channel-info {
        opacity: 1;
      }
    }

    ${({ canTouch, uiControls }) =>
      uiControls &&
      (canTouch
        ? css`
            .control-btn {
              opacity: 1;
            }
          `
        : css`
            .control-btn {
              opacity: 0;
            }
          `)}
  }

  ${({ isFullscreenSplashPage }) =>
    isFullscreenSplashPage &&
    css`
      margin-top: 0;
      height: 100%;
      max-height: 100vh;
      box-shadow: none;
      position: relative;
    `};
`;

const RatioWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  bottom: ${({ isDocked }) => (isDocked ? '30px' : '')};
  right: ${({ isDocked, isMobile }) => (!isMobile && isDocked ? '-10px' : '')};
  min-width: ${({ isDocked, playerIsFullscreen }) =>
    isDocked && playerIsFullscreen ? '100%' : isDocked ? '326px' : ''};
  min-height: ${({ isDocked, playerIsFullscreen }) =>
    isDocked && playerIsFullscreen ? '100%' : isDocked ? '184.5px' : ''};
  max-width: ${({ isDocked, playerIsFullscreen }) =>
    isDocked && playerIsFullscreen ? '100%' : isDocked ? '417px' : ''};
  margin: auto;
  box-shadow: ${({ playerIsFullscreen }) =>
    playerIsFullscreen ? 'none' : '0 19px 38px rgba(0,0,0,0.30), 0 15px 12px rgba(0,0,0,0.22)'};
  transition: height 0.3s linear, padding-top 0.3s linear;

  // fix full screen player (prop: internalFullscreen) when clicked from docked player
  ${({ internalFullscreen, isFullscreenSplashPage }) =>
    (internalFullscreen || isFullscreenSplashPage) &&
    css`
      max-height: 56.25vw;
      top: 50%;
      transform: translateY(-50%);
      min-height: auto;
      bottom: auto;
    `};
  ${({ isDocked, internalFullscreen }) =>
    isDocked &&
    !internalFullscreen &&
    css`
      max-height: 236px;
    `};

  ${({ isHeadless }) =>
    isHeadless &&
    css`
      top: 0;
      transform: none;
    `};

  // for Homepage L1 player
  @media (max-width: 160px) {
    height: inherit !important;
  }

  @media (max-width: 767px) {
    right: ${({ isDocked, isMobile }) => (!isMobile && isDocked ? 0 : '')};
    max-width: none;
  }
  @media (max-height: 375px) and (max-width: 767px) and (orientation: landscape) {
    ${({ isMobile, playerIsFullscreen, isEmbed }) =>
      (isMobile && playerIsFullscreen) || isEmbed ? 'height: 69vh' : ''};
  }
  @media (max-height: 321px) and (max-width: 767px) and (orientation: landscape) {
    ${({ isMobile, playerIsFullscreen, isEmbed }) =>
      (isMobile && playerIsFullscreen) || isEmbed ? 'height: 64vh' : ''};
  }
  @media (max-width: 960px) and (orientation: landscape) {
    ${({ isMobile }) =>
      isMobile &&
      css`
        height: 96vh;
        max-height: 100vw;
        top: 0 !important;
        transform: none !important;
      `};
  }
  @media (max-height: 743px) and (max-width: 767px) {
    transform: ${({ isMobile, isDocked }) => (!isMobile && isDocked ? 'none' : '')};
  }

  // fix positioning of docked player on large-screen mobile devices in landscape mode
  @media (min-width: 768px) and (max-width: 959px) and (orientation: landscape) {
    ${({ isDocked, isMobile }) =>
      isDocked &&
      isMobile &&
      css`
        bottom: 50px;
        right: 0px;
        height: 100%;
      `};
  }

  // on mobile landscape, expand player to full screen
  ${({ isMobile }) =>
    isMobile &&
    css`
      @media (max-width: 767px) and (orientation: landscape) {
        bottom: 0;
        right: 0;
        min-width: 100%;
        min-height: 100%;
        max-width: 100%;
        max-height: 100%;
        box-shadow: none;
      }
    `}
`;

const MinimizedOverlay = styled.div`
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
  height: 100%;
  opacity: 0;
  position: absolute;
  top: 0;
  transition: opacity 0.3s linear;
  width: 100%;

  @media (max-width: 480px), (orientation: landscape) and (max-width: 767px) {
    display: none;
  }
`;

// this animation lasts 2s, so 15% (100 - 85) is 0.3s.
const overlayAnimation = keyframes`
  0% { opacity: 0 }
  25% { opacity: 1 }
  85% { opacity: 1 }
`;

const FixPosition = styled.div`
  background-color: #000000;
  box-shadow: ${({ playerIsFullscreen }) => (playerIsFullscreen ? 'none' : ' 0 2px 4px 0 #000000')};
  height: 100%;
  width: 100%;
  max-height: ${({ isDocked }) => (isDocked ? '236px' : '')};
  overflow: ${({ isCCMenuOpen }) => (isCCMenuOpen ? 'visible' : 'hidden')};
  position: absolute;
  top: ${({ playerIsFullscreen }) => (playerIsFullscreen ? '' : 0)};
  transition: top 0.3s linear;
  border-radius: ${({ playerIsFullscreen }) => (playerIsFullscreen ? 0 : '5px')};
  box-shadow: ${({ playerIsFullscreen }) =>
    playerIsFullscreen ? 'none' : '0px 2px 9px 5px rgba(0, 0, 0, 0.45)'};

  .jwplayer {
    background-color: transparent;
    height: 100%;
    max-height: 100%;
    position: static;
  }

  .jw-ie video {
    height: 100% !important;
    margin: 0 !important;
    position: static !important;
    transform: none !important;
    width: 100% !important;
  }

  // for Homepage L1 player
  @media (max-width: 160px) {
    height: inherit !important;
  }

  @media (max-width: 480px), (orientation: landscape) and (max-width: 767px) {
    width: 100%;
    height: ${({ playerIsFullscreen }) => (playerIsFullscreen ? '100%' : '62px')};
    bottom: ${({ isDocked }) => (isDocked ? '-40px' : '')};
    top: ${({ isDocked }) => (isDocked ? 'auto' : 0)};
    border-radius: ${({ isDocked }) => (isDocked ? 0 : '')};
  }

  @media (max-width: 480px), (max-height: 378px) and (orientation: landscape) {
    ${({ isEmbed }) => (isEmbed ? 'height: calc(100vh - 64px)' : '')};
  }

  ${({ playerIsFullscreen }) =>
    !playerIsFullscreen &&
    css`
      ${MinimizedOverlay} {
        cursor: pointer;
        animation: 2s linear ${overlayAnimation};
      }

      &:hover ${MinimizedOverlay} {
        opacity: 1;
      }
    `};

  // on mobile landscape, expand player to full screen
  ${({ isMobile }) =>
    isMobile &&
    css`
    @media (orientation: landscape) and (max-width: 767px) {
      width: 100%;
      height: 100%;
      top: 0;
      max-height: 100%;
      box-shadow: none;
  `}

  ${({ internalFullscreen, isFullscreenSplashPage }) =>
    (internalFullscreen || isFullscreenSplashPage) &&
    css`
      max-height: 100%;
    `};
`;

const ResizableWrapper = styled.div`
  bottom: 0;
  box-sizing: border-box;
  height: ${({ playerIsFullscreen, isDocked }) =>
    playerIsFullscreen && !isDocked ? '100%' : '14vw'};
  position: absolute;
  right: ${({ playerIsFullscreen }) => (playerIsFullscreen ? 0 : '54px')};
  transition: none 0.3s linear;
  transition-property: bottom, height, right, width;
  width: 100%;
  z-index: 25;

  ${({ playerIsFullscreen }) =>
    !playerIsFullscreen &&
    css`
      width: 25%;
      min-width: 200px;

      @media (max-width: 480px) {
        width: 100%;
        right: 0;
        bottom: 0;
        overflow: hidden;
      }
    `}

  ${({ internalFullscreen, isFullscreenSplashPage }) =>
    (internalFullscreen || isFullscreenSplashPage) &&
    css`
      height: 100%;
    `};

  @media (orientation: landscape) and (max-width: 767px) {
    height: 100%;
    right: 0;
    width: 100%;
    min-width: 100%;
  }
`;

const Centered = styled.div`
  display: flex;
  height: 100%;
  text-align: center;
  vertical-align: middle;

  > * {
    margin: auto 0;
    flex: 0 0 auto;
    &:first-child {
      margin-left: auto;
    }
    &:last-child {
      margin-right: auto;
    }
  }

  ${({ playerIsFullscreen }) =>
    !playerIsFullscreen &&
    css`
      @media (min-width: 480px) {
        display: none;
      }
    `}
`;

const Circle = styled.span`
  background: #000;
  border-radius: 50%;
  display: inline-block;
  height: 2em;
  margin-right: 0.3em;
  width: 2em;
  > svg {
    transform: rotate(-45deg);
    position: relative;
    top: 4px;
  }
`;

const Label = styled.span`
  font-size: 0.6em;
  font-weight: bold;
  height: 1.3em; // IE fix
  color: #fff;
`;

const VideoWrapper = styled.div`
  display: inline-block;
  height: 100%;
  width: 100%;
  /* @-moz-document url-prefix() {
    width: 100%;
  } */
  position: relative;
  left: 50%;
  transform: translateX(-50%);

  @media (max-width: 480px), (orientation: landscape) and (max-width: 767px) {
    width: ${({ playerIsFullscreen }) => (playerIsFullscreen ? '100%' : '110px')};
    left: 0;
    transform: translate(0);
  }

  // on mobile landscape, expand player to full screen
  ${({ isMobile }) =>
    isMobile &&
    css`
      @media (orientation: landscape) and (max-width: 767px) {
        width: 100%;
      }
    `}
`;

const SizeHelper = styled(AssetsFileResolver)`
  display: block;
  height: 100%;
  width: auto;
  visibility: hidden;
`;

const VideoInner = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;


class PlayerComp extends React.Component {
  static propTypes = {
    clearError: PropTypes.func.isRequired,
    enteredFullscreen: PropTypes.func.isRequired,
    exitedFullscreen: PropTypes.func.isRequired,
    ccSubmenuOpened: PropTypes.func.isRequired,
    ccMenuOnReset: PropTypes.func.isRequired,
    ccSetIndex: PropTypes.func.isRequired,
    ccMenuOpened: PropTypes.func.isRequired,
    ccMenuClosed: PropTypes.func.isRequired,
    featuredImage: PropTypes.string,
    viralClips: PropTypes.arrayOf(PropTypes.shape({})),
    hasError: PropTypes.bool.isRequired,
    hidePlayer: PropTypes.bool.isRequired,
    internalEnteredFullscreen: PropTypes.func.isRequired,
    internalExitedFullscreen: PropTypes.func.isRequired,
    playerIsFullscreen: PropTypes.bool.isRequired,
    internalFullscreen: PropTypes.bool.isRequired,
    isDocked: PropTypes.bool.isRequired,
    isBuffering: PropTypes.bool.isRequired,
    isPaused: PropTypes.bool.isRequired,
    isPlaying: PropTypes.bool.isRequired,
    isChatBarShown: PropTypes.bool.isRequired,
    isPageTrending: PropTypes.bool.isRequired,
    guideVisible: PropTypes.bool.isRequired,
    kind: PropTypes.string,
    mute: PropTypes.func.isRequired,
    nowPlaying: PropTypes.shape({}),
    nextChannelIndex: PropTypes.number.isRequired,
    query: PropTypes.shape({}),
    onBuffer: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    onFullscreen: PropTypes.func,
    unmute: PropTypes.func.isRequired,
    url: PropTypes.string,
    isEpisode: PropTypes.bool.isRequired,
    episodeDialogInfo: PropTypes.shape({}),
    isViral: PropTypes.bool.isRequired,
    isEmbed: PropTypes.bool.isRequired,
    isHeadless: PropTypes.bool.isRequired,
    stop: PropTypes.func.isRequired,
    isStopped: PropTypes.bool.isRequired,
    isPausedOrPlaying: PropTypes.bool.isRequired,
    nextEpisode: PropTypes.shape({
      _id: PropTypes.string.isRequired,
    }),
    vodPlayItem: PropTypes.func.isRequired,
    volumeChange: PropTypes.func.isRequired,
    clickPlay: PropTypes.func.isRequired,
    clickPause: PropTypes.func.isRequired,
    uiLoaded: PropTypes.func.isRequired,
    isMuted: PropTypes.bool.isRequired,
    isCCMenuOpen: PropTypes.bool.isRequired,
    currentCCMenu: PropTypes.instanceOf(Array).isRequired,
    currentCCMenuName: PropTypes.string.isRequired,
    ccMenuSelectedIndexes: PropTypes.instanceOf(Array).isRequired,
    volume: PropTypes.number.isRequired,
    volumeBeforeMute: PropTypes.number.isRequired,
    changeChannelUrlSlug: PropTypes.func.isRequired,
    playTVChannel: PropTypes.func.isRequired,
    currentPlayingChannelId: PropTypes.string,
    playNewsChannel: PropTypes.func.isRequired,
    playComedyChannel: PropTypes.func.isRequired,
    playTVChannelAtIndex: PropTypes.func.isRequired,
    viralNextChannel: PropTypes.shape({}),
    closeEpisodeDialog: PropTypes.func.isRequired,
  };

  static defaultProps = {
    currentPlayingChannelId: null,
    featuredImage: null,
    viralClips: [],
    kind: null,
    nowPlaying: {},
    query: {},
    episodeDialogInfo: null,
    onFullscreen: null,
    url: null,
    nextEpisode: null,
    viralNextChannel: {},
  };

  hidden = 'hidden';
  visibilityChange = 'visibilitychange';

  constructor(props) {
    super(props);

    const { isIos, isAndroid } = checkMobileOperatingSystem();
    const isMobile = isIos || isAndroid;

    this.state = {
      adMap: {},
      playerContext: this.createPlayerContext(props),
      currentlyPlayingAdIdAccordingToStream: '',
      isMobile,
      isAndroid,
      isBufferingAfterPause: false,
    };
    if (typeof document.msHidden !== 'undefined') {
      this.hidden = 'msHidden';
      this.visibilityChange = 'msvisibilitychange';
    } else if (typeof document.webkitHidden !== 'undefined') {
      this.hidden = 'webkitHidden';
      this.visibilityChange = 'webkitvisibilitychange';
    }
  }

  containerRef = React.createRef();

  bufferingTimeout = null;
  errorCount = 0;

  // for iOS: continue play when player gets the focus (W50-1178)
  handleVisibilityChange = () => {
    if (!document[this.hidden]) {
      this.play({ doNotTrack: true });
    }
  };

  componentDidMount() {
    this.props.uiLoaded();
    document.addEventListener(this.visibilityChange, this.handleVisibilityChange, false);
    fetch(process.env.CLICKABLE_ADS_URL)
      .then((response) => response.json())
      .then((adMap) => {
        this.setState({ adMap });
        return adMap;
      });
  }

  componentWillUnmount() {
    document.removeEventListener(this.visibilityChange, this.handleVisibilityChange);
  }

  setTimer = () => {
    const { nextChannelIndex, playNewsChannel, playTVChannelAtIndex } = this.props;
    if (this.timerHandle) {
      return;
    }
    this.timerHandle = setTimeout(() => {
      if (nextChannelIndex) {
        playTVChannelAtIndex(nextChannelIndex);
      } else {
        playNewsChannel();
      }
    }, VIRAL_CLIP_RESOLVE_TIME);
  };

  clearTimer = () => {
    if (this.timerHandle) {
      clearTimeout(this.timerHandle);
      this.timerHandle = 0;
    }
  };

  componentWillReceiveProps(nextProps) {
    const {
      isBuffering,
      isViral,
      isPaused,
      isStopped,
      closeEpisodeDialog,
      episodeDialogInfo,
      isDocked,
      stop,
    } = this.props;

    if ((nextProps.isBuffering || nextProps.isPlaying) && isPaused) {
      this.setState({ isBufferingAfterPause: true });
    }

    if (nextProps.isBuffering !== isBuffering) {
      if (nextProps.isBuffering) {
        // Too much time buffering. Let's reload
        this.bufferingTimeout = setTimeout(this.retryPlay, MAX_BUFFERING_TIME_IN_MS);
      } else if (this.bufferingTimeout) {
        clearTimeout(this.bufferingTimeout);
        this.setState({ isBufferingAfterPause: false });
        this.bufferingTimeout = null;
      }
    }
    // 10 sec delay at the end of viral clip
    if (isViral && nextProps.isPaused !== isPaused) {
      stop();
      this.setTimer();
    }

    // cancel viral autoplay timer if user played another clip before 10 sec
    if (isViral && isStopped && nextProps.isStopped !== isStopped) {
      this.clearTimer();
    }

    // info modal timer
    if (nextProps.episodeDialogInfo !== episodeDialogInfo && !episodeDialogInfo) {
      const dialogCloseTimeout = !isDocked ? 15000 : this.state.isMobile ? 15000 : 60000;
      setTimeout(() => {
        this.enteredFullscreen();
        closeEpisodeDialog();
      }, dialogCloseTimeout);
    }

    // embed mute/unmute
    if (nextProps.isMuted !== this.props.isMuted) {
      if (nextProps.isMuted) {
        this.mute();
      } else {
        this.unMute();
      }
    }

    if (nextProps.isPageTrending !== this.props.isPageTrending) {
      const isVod = nextProps.kind === constants.player.PLAYER_CONTENT_KIND.VOD;
      if (nextProps.isPageTrending) {
        if (isVod) {
          this.pause();
        }
        this.mute();
      } else {
        if (isVod) {
          this.play();
        }
        this.unMute();
      }
    }

    this.updateContext({
      kind: nextProps.kind,
      docked: !nextProps.playerIsFullscreen,
    });
  }

  onBuffer = (e) => {
    this.updateContext({
      playerState: 'buffering',
    });

    if (this.props.onBuffer) {
      this.props.onBuffer(e);
    }
  };

  onBufferChange = ({ duration }) => {
    if (duration !== this.state.playerContext.duration) {
      this.updateContext({
        duration,
      });
    }
  };

  onPause = () => {
    this.updateContext({
      playerState: 'paused',
    });
  };

  onPlay = () => {
    this.updateContext({
      playerState: 'playing',
      autoPlayBlocked: false,
      overlayFrozen: false,
    });
  };

  onPlayerDestroy = () => {
    this.removeListeners(this.player, this.playerEvents);
    this.player = null;
  };

  onPlayerReady = (player, Events) => {
    this.player = player;
    this.playerEvents = Events;
    this.setState({
      playerContext: this.createPlayerContext(this.props, player),
    });
    this.player.setVolume(this.props.volume);
    this.addListeners(player, Events);
  };

  onPlayerComplete = () => {
    if (this.props.isEpisode && this.props.nextEpisode) {
      // eslint-disable-next-line no-underscore-dangle
      this.props.vodPlayItem(this.props.nextEpisode._id);
    } else {
      this.props.changeChannelUrlSlug();
      this.props.playTVChannel();
      this.setState({
        playerContext: this.createPlayerContext(this.props, this.player),
      });
    }
  };

  onIdle = () => {
    this.updateContext({
      playerState: 'idle',
    });
  };

  onTime = ({ duration, position } = {}) => {
    this.updateContext({
      duration,
      position,
    });
  };

  onMute = ({ mute } = {}) => {
    this.updateContext({
      isMuted: mute,
    });
  };

  onFullscreen = ({ fullscreen } = {}) => {
    this.updateContext({
      isFullscreen: fullscreen,
    });
    if (this.props.onFullscreen) {
      this.props.onFullscreen(fullscreen);
    }
    if (fullscreen) {
      this.props.internalEnteredFullscreen();
    } else {
      this.props.internalExitedFullscreen();
    }
  };

  onFullscreenChange = () => {
    if (fscreen.fullscreenElement !== null) {
      this.onFullscreen({ fullscreen: true });

      this.setState({
        previousPlayerIsFullscreen: this.props.playerIsFullscreen,
      });

      if (!this.props.playerIsFullscreen) {
        this.props.enteredFullscreen();
      }
    } else {
      this.onFullscreen({ fullscreen: false });

      if (!this.state.previousPlayerIsFullscreen) {
        this.props.exitedFullscreen();
      }
    }
  };

  setCurrentlyPlayingAdIdAccordingToStream = (str) => {
    this.setState({ currentlyPlayingAdIdAccordingToStream: str });
  };

  // when a submenu item is clicked to open the next submenu
  onCCSubmenuOpen = () => {
    this.props.ccSubmenuOpened();
  };

  // called for when the menu items are hovered on to set the index
  onCCSetIndex = (index) => {
    this.props.ccSetIndex(index);
  };

  // If the overlay is clicked then it resets the CC settings menu
  onCCOverlayClick = () => {
    this.props.ccMenuOnReset();
  };

  // when the cc settings button is clicked
  onCCMenuOpen = () => {
    this.props.ccMenuOpened();
  };

  onCCMenuClose = () => {
    this.props.ccMenuClosed();
  };

  getErrorComponent = () => <UnknownError retry={this.retryPlay} />;

  onAutoPlayBlocked = (player, Events) => {
    if (!this.player) {
      this.player = player;
      this.playerEvents = Events;
    }

    this.updateContext({
      autoPlayBlocked: true,
      overlayFrozen: true,
    });
  };

  volumeChange = (oldVolume, volume) => {
    if (oldVolume !== volume) {
      const options = {
        up: volume > oldVolume,
        level: volume,
      };
      this.sendChangedVolume(options);
    }
  };

  sendChangedVolume = debounce((options) => this.props.volumeChange(options), 800, {
    trailing: true,
    leading: false,
  });

  setVolume = (newVolume, wasMuted) => {
    const volume = Math.trunc(newVolume);
    if (this.player) {
      this.volumeChange(wasMuted ? 0 : this.player.getVolume(), volume);
      this.player.setVolume(volume);
    }
    this.updateContext({
      volume,
    });
  };

  play = (payload) => {
    this.props.clickPlay(payload);
    if (this.player) {
      this.player.play();
    }
  };

  pause = () => {
    this.props.clickPause();
    if (this.player) {
      this.player.pause();
    }
  };

  togglePlay = () => {
    if (this.player) {
      if (this.state.playerContext.isPlaying) {
        this.pause();
      } else {
        this.play();
      }
    }
  };

  seek = (value) => {
    if (this.player) {
      this.player.pause();
      requestAnimationFrame(() => {
        this.player.seek(value);
        this.player.play();
      });
    }
  };

  mute = () => {
    this.props.mute();
    this.setVolume(0);
    if (this.player) {
      this.player.mute();
    }
    this.onMute({ mute: true });
  };

  unMute = () => {
    if (this.player) {
      this.player.unmute();
    }
    this.props.unmute();
    this.setVolume(this.props.volumeBeforeMute, true);
    this.onMute({ mute: false });
  };

  fullscreen = () => {
    if (this.player) {
      if (fscreen.fullscreenEnabled && this.props.hidePrivacyNotification) {
        fscreen.requestFullscreen(this.containerRef.current);
      }
    }
  };

  exitFullscreen = () => {
    fscreen.exitFullscreen();
  };

  freezeOverlay = () => {
    this.updateContext({
      overlayFrozen: true,
    });
  };

  unfreezeOverlay = () => {
    this.updateContext({
      overlayFrozen: false,
    });
  };

  retryPlay = () => {
    if (this.player) {
      this.props.clearError();

      const { url } = this.props;
      this.player.load({
        file: url,
      });

      this.player.play();
    }
  };

  handleError = (error) => {
    if (
      error.message.indexOf('permission') > -1 ||
      error.message.indexOf('500 Internal Server Error') > -1
    ) {
      console.warn('Not retrying on 403/500 stream error');
      this.props.onError(error);
    } else if (this.errorCount < RETRIES_ON_ERROR) {
      this.props.onBuffer();
      this.errorCount += 1;
      setTimeout(this.retryPlay, RETRY_RATE_IN_MS);
    } else {
      console.warn(`Giving up after ${RETRIES_ON_ERROR} retries`);
      this.props.onError(error);
    }
  };

  createPlayerContext = (props, player) => ({
    kind: props.kind,
    docked: !props.playerIsFullscreen,
    isPlaying: player && player.isPlaying(),
    isMuted: this.props.isMuted,
    isFullscreen: props.internalFullscreen,
    volume: (this.player && this.player.getVolume()) || this.props.volume,
    volumeBeforeMute: props.volumeBeforeMute,
    duration: 0,
    position: 0,
    play: this.play,
    pause: this.pause,
    togglePlay: this.togglePlay,
    seek: this.seek,
    mute: this.mute,
    unMute: this.unMute,
    setVolume: this.setVolume,
    fullscreen: this.fullscreen,
    exitFullscreen: this.exitFullscreen,
    freezeOverlay: this.freezeOverlay,
    unfreezeOverlay: this.unfreezeOverlay,
    containerRef: this.containerRef,
  });

  updateContext = (newContext) => {
    const playerContext = Object.assign({}, this.state.playerContext || {}, newContext);
    playerContext.isPlaying = this.player && this.player.isPlaying();
    playerContext.volume =
      newContext.volume || (this.player && this.player.getVolume()) || this.props.volume;

    this.setState({
      playerContext,
    });
  };

  addListeners = (player, Events) => {
    player.on(Events.PLAYER_ERROR, this.handleError);
    player.on('bufferChange', this.onBufferChange);
  };

  removeListeners = (player, Events) => {
    if (player) {
      player.off(Events.PLAYER_ERROR, this.handleError);
      player.off('bufferChange', this.onBufferChange);
    }
  };

  enteredFullscreen = () => {
    if (!this.props.playerIsFullscreen) {
      this.props.enteredFullscreen();
    }
  };

  render() {
    const {
      currentPlayingChannelId,
      playerIsFullscreen,
      hasError,
      hidePlayer,
      internalFullscreen,
      exitedFullscreen,
      url,
      kind,
      featuredImage,
      viralClips,
      isCCMenuOpen,
      currentCCMenu,
      currentCCMenuName,
      ccMenuSelectedIndexes,
      t,
      isEmbed,
      isHeadless,
      isDocked,
      isViral,
      isStopped,
      viralNextChannel,
      isFullscreenSplashPage,
      exitFullscreenSplashPage,
      playTVChannelAtIndex,
      dispatchPlayViralClip,
      changeViralUrl,
      isPausedOrPlaying,
    } = this.props;

    const {
      adMap,
      currentlyPlayingAdIdAccordingToStream,
      isMobile,
      isAndroid,
      isBufferingAfterPause,
    } = this.state;
    const isVod = kind === constants.player.PLAYER_CONTENT_KIND.VOD;
    const clickableAdData =
      adMap[currentlyPlayingAdIdAccordingToStream] &&
      adMap[currentlyPlayingAdIdAccordingToStream].web;

    const channels = clickableAdData && clickableAdData.channels;

    // Clickable ads should default to being shown on the current channel.
    // If there is a whitelist and the current channel isn't on it, or if there is a blacklist
    // and the current channel is on it, don't render the clickable button.
    let isClickableAdEnabledOnCurrentChannel = true;
    if (
      (channels &&
        channels.whitelist &&
        channels.whitelist.indexOf(currentPlayingChannelId) === -1) ||
      (channels && channels.blacklist && channels.blacklist.indexOf(currentPlayingChannelId) >= 0)
    ) {
      isClickableAdEnabledOnCurrentChannel = false;
    }

    // [WWW-1678] hide loading splashscreen when user comes back from collapsed [chrome] browser and paused show in iPhones
    const active = !isPausedOrPlaying && !isBufferingAfterPause;

    return (
      <PlayerContext.Provider value={this.state.playerContext}>
        <Wrapper
          {...this.props}
          className="video-player-wrapper"
          ref={this.containerRef}
          isMobile={isMobile}
          isHeadless={isHeadless}
        >
          <EventListener target={fscreen} onFullscreenchange={this.onFullscreenChange} />
          <ResizableWrapper {...this.props} isMobile={isMobile}>
            <RatioWrapper
              {...this.props}
              isMobile={isMobile}
              isHeadless={isHeadless}
              isDocked={isDocked}
              onClick={this.enteredFullscreen}
            >
              <FixPosition {...this.props} isMobile={isMobile}>
                {isViral && isStopped ? (
                  <ViralEndVideoNavigatorWrapper
                    viralClips={viralClips}
                    viralNextChannel={viralNextChannel}
                    playTVChannelAtIndex={playTVChannelAtIndex}
                    changeViralUrlSlug={changeViralUrl}
                    playViralClip={dispatchPlayViralClip}
                  />
                ) : (
                  <LoadingIndicator
                    active={active}
                    featuredImage={featuredImage}
                    isMobile={isMobile}
                  />
                )}
                <ErrorBoundary
                  error={hasError ? new Error() : null}
                  getErrorComponent={this.getErrorComponent}
                  overlayMode
                >
                  <VideoWrapper playerIsFullscreen={playerIsFullscreen} isMobile={isMobile}>
                    {/* Image needed to keep the video proportions right. */}
                    <SizeHelper path="16x9.png" />
                    <VideoInner>
                      {url && (
                        <VideoPlayer
                          url={url}
                          hidePlayer={hidePlayer}
                          posterUrl={featuredImage}
                          onComplete={this.onPlayerComplete}
                          onDestroy={this.onPlayerDestroy}
                          onReady={this.onPlayerReady}
                          onTime={this.onTime}
                          onPlay={this.onPlay}
                          onPause={this.onPause}
                          mutePlayer={this.mute}
                          unmutePlayer={this.unMute}
                          onBuffer={this.onBuffer}
                          onAutoPlayBlocked={this.onAutoPlayBlocked}
                          setCurrentlyPlayingAdIdAccordingToStream={
                            this.setCurrentlyPlayingAdIdAccordingToStream
                          }
                        />
                      )}
                    </VideoInner>
                  </VideoWrapper>
                  <MinimizedOverlay>
                    <Centered>
                      <Circle>
                        <ArrowUpward style={{ color: '#fff' }} />
                      </Circle>
                      <Label>{t('expand_popup_window')}</Label>
                    </Centered>
                  </MinimizedOverlay>
                  <PlayerOverlay
                    playerIsFullscreen={playerIsFullscreen}
                    exitFullscreen={exitedFullscreen}
                    isCCMenuOpen={isCCMenuOpen}
                    currentCCMenu={currentCCMenu}
                    currentCCMenuName={currentCCMenuName}
                    ccMenuSelectedIndexes={ccMenuSelectedIndexes}
                    onCCSubmenuOpen={this.onCCSubmenuOpen}
                    onCCOverlayClick={this.onCCOverlayClick}
                    onCCSetIndex={this.onCCSetIndex}
                    onCCMenuOpen={this.onCCMenuOpen}
                    onCCMenuClose={this.onCCMenuClose}
                    isMobile={isMobile}
                    isVod={isVod}
                    isEmbed={isEmbed}
                    isAndroid={isAndroid}
                    isHeadless={isHeadless}
                    isFullscreenSplashPage={isFullscreenSplashPage}
                    exitFullscreenSplashPage={exitFullscreenSplashPage}
                    internalFullscreen={internalFullscreen}
                  />
                  {isPausedOrPlaying && // Don't show during buffering, etc.
                    playerIsFullscreen && // Don't show in PIP view.
                    clickableAdData &&
                    isClickableAdEnabledOnCurrentChannel && (
                      <PlayerCTA
                        adId={currentlyPlayingAdIdAccordingToStream}
                        burnedInButton={clickableAdData.burnedInButton}
                        buttonText={clickableAdData.buttonText}
                        endDateTime={clickableAdData.endDateTime}
                        internalFullscreen={internalFullscreen}
                        isMobile={isMobile}
                        startDateTime={clickableAdData.startDateTime}
                        targetUrl={clickableAdData.targetUrl}
                        currentPlayingChannelId={currentPlayingChannelId}
                      />
                    )}
                </ErrorBoundary>
              </FixPosition>
            </RatioWrapper>
          </ResizableWrapper>
        </Wrapper>
      </PlayerContext.Provider>
    );
  }
}

export const PlayerComponent = withNamespaces()(PlayerComp);
